import { findAll, prefersReducedMotion } from './dom.js';

const TABS_SELECTOR = '[data-bp-tabs]';
const BUTTON_SELECTOR = '[data-bp-tab]';
const PANEL_SELECTOR = '[aria-labelledby], [role="region"]';
const ACTIVE_CLASS = 'is-active';
const ENTERING_CLASS = 'is-entering';
const READY_ATTRIBUTE = 'data-bp-ready';
const READY_VALUE = 'tabs';
const CYCLE_INTERVAL = 7000;
const CYCLE_COVERAGE = 0.5;
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const STOP_EVENTS = ['click', 'keydown', 'touchstart'];

function panelFor(button, root) {
  const id = button.getAttribute('aria-controls');
  if (!id) return null;
  const candidates = root.querySelectorAll(PANEL_SELECTOR);
  for (const candidate of candidates) {
    if (candidate.getAttribute('id') === id) return candidate;
  }
  return null;
}

function collectPairs(container) {
  const pairs = [];
  for (const button of container.querySelectorAll(BUTTON_SELECTOR)) {
    const panel = panelFor(button, container);
    if (panel) pairs.push({ button, panel });
  }
  return pairs;
}

function activate(pairs, selected, replay) {
  for (const pair of pairs) {
    const isSelected = pair === selected;
    pair.button.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
    pair.button.classList[isSelected ? 'add' : 'remove'](ACTIVE_CLASS);
    pair.panel.classList[isSelected ? 'add' : 'remove'](ACTIVE_CLASS);
    pair.button.classList.remove(ENTERING_CLASS);
    pair.panel.classList.remove(ENTERING_CLASS);
  }

  if (!replay || !selected) return;
  // Reading a layout value between the removal and the re-add restarts the entrance animation, so returning to a pair replays it.
  void selected.panel.offsetWidth;
  selected.button.classList.add(ENTERING_CLASS);
  selected.panel.classList.add(ENTERING_CLASS);
}

/** Timer for the unattended rotation; every gate is a plain setter so the stop conditions stay testable without a browser. */
export function createAutoCycle({ advance, interval = CYCLE_INTERVAL, schedule, cancel } = {}) {
  const start = schedule ?? ((handler, delay) => setTimeout(handler, delay));
  const clear = cancel ?? ((handle) => clearTimeout(handle));
  const state = { sectionVisible: false, pageVisible: true, pointerInside: false, stopped: false };
  let handle = null;

  function permitted() {
    return !state.stopped && state.sectionVisible && state.pageVisible && !state.pointerInside;
  }

  function tick() {
    handle = null;
    if (!permitted()) return;
    advance();
    handle = start(tick, interval);
  }

  function sync() {
    if (permitted()) {
      if (handle === null) handle = start(tick, interval);
      return;
    }
    if (handle !== null) {
      clear(handle);
      handle = null;
    }
  }

  return {
    setSectionVisible(value) {
      state.sectionVisible = Boolean(value);
      sync();
    },
    setPageVisible(value) {
      state.pageVisible = Boolean(value);
      sync();
    },
    setPointerInside(value) {
      state.pointerInside = Boolean(value);
      sync();
    },
    stop() {
      state.stopped = true;
      sync();
    },
    isScheduled() {
      return handle !== null;
    },
    isStopped() {
      return state.stopped;
    },
  };
}

// A section taller than the viewport can never reach a ratio of one half, so coverage is measured against the shorter of the two.
function covered(entry, viewportHeight) {
  const own = entry.boundingClientRect.height;
  const reference = Math.min(own || 0, viewportHeight || 0);
  if (!reference) return false;
  return entry.intersectionRect.height / reference >= CYCLE_COVERAGE;
}

function attachAutoCycle(container, pairs, select) {
  const doc = container.ownerDocument;
  const view = doc && doc.defaultView;
  if (!view || typeof view.matchMedia !== 'function' || typeof view.IntersectionObserver !== 'function') return null;
  if (prefersReducedMotion(view)) return null;
  const hoverable = view.matchMedia(HOVER_QUERY);

  const cycle = createAutoCycle({
    schedule: (handler, delay) => view.setTimeout(handler, delay),
    cancel: (handle) => view.clearTimeout(handle),
    // A pointer already inside on load never crosses a boundary, so the enter that holds the rotation never fires.
    // The hover state is read at the tick instead: the rotation holds and the pointed-at row is selected, not the next one.
    advance() {
      if (container.matches(':hover')) {
        const hovered = pairs.find((pair) => pair.button.matches(':hover'));
        if (hovered && hoverable.matches && !hovered.button.classList.contains(ACTIVE_CLASS)) select(hovered);
        return;
      }
      const current = pairs.findIndex((pair) => pair.button.classList.contains(ACTIVE_CLASS));
      select(pairs[(current + 1) % pairs.length]);
    },
  });

  const observer = new view.IntersectionObserver((entries) => {
    for (const entry of entries) cycle.setSectionVisible(covered(entry, view.innerHeight));
  }, { threshold: Array.from({ length: 21 }, (_, step) => step / 20) });
  observer.observe(container);

  cycle.setPageVisible(doc.visibilityState !== 'hidden');
  doc.addEventListener('visibilitychange', () => cycle.setPageVisible(doc.visibilityState !== 'hidden'));
  container.addEventListener('pointerenter', () => cycle.setPointerInside(true));
  container.addEventListener('pointerleave', () => cycle.setPointerInside(false));

  // One deliberate act ends the rotation for the life of the page.
  const surrender = () => {
    cycle.stop();
    observer.disconnect();
  };
  for (const type of STOP_EVENTS) container.addEventListener(type, surrender, { passive: true });

  return cycle;
}

export function initializePortalTabs(root) {
  const containers = findAll(root, TABS_SELECTOR);

  let initialized = 0;
  for (const container of containers) {
    if (container.getAttribute(READY_ATTRIBUTE) === READY_VALUE) continue;

    const pairs = collectPairs(container);
    if (pairs.length < 2) continue;

    const authored = pairs.find((pair) => pair.button.classList.contains(ACTIVE_CLASS));
    activate(pairs, authored ?? pairs[0], false);

    const select = (pair) => activate(pairs, pair, true);
    // A pointer resting on a row selects it, but only where a row can be pointed at without being committed to:
    // a coarse pointer reports its enter on the tap that follows, so the phone accordion keeps the tap path alone.
    const hoverable = container.ownerDocument?.defaultView?.matchMedia?.(HOVER_QUERY);
    for (const pair of pairs) {
      pair.button.addEventListener('click', (event) => {
        event.preventDefault();
        select(pair);
      });
      // A move rather than an enter: a pointer already resting on a row, or one the section scrolls under, never fires an enter at all.
      pair.button.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || !hoverable?.matches) return;
        // Re-entering the row that is already selected would replay its entrance for no change.
        if (pair.button.classList.contains(ACTIVE_CLASS)) return;
        select(pair);
      });
    }

    attachAutoCycle(container, pairs, select);

    container.setAttribute(READY_ATTRIBUTE, READY_VALUE);
    initialized += 1;
  }

  return initialized;
}
